import { SCol, SFiltersPanel, SRow, SSelect } from "@avalara/skylab-sdk/react";
import classnames from "classnames";
import React, { useCallback, useMemo, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { testIDs } from "../../../end-to-end-tests/constants/testIDs";
import { selectStates } from "../../app/commonSlice";
import {
    clearEcmsFiltersState,
    EcmsFiltersState,
    fetchEcms,
    selectEcmsFiltersState,
    selectEcmsIsLoading,
    setEcmsFiltersState,
    setHasFilterChips,
} from "../../app/ecmsSlice";
import { AppDispatch } from "../../app/types";
import { preserveScrollPosition } from "../../shared/Utils";
import ECMSFilterChip from "./ECMSFilterChip";

interface ECMSFilterProps {
    hasFilterChips: boolean;
    openFilters: boolean;
    onToggleFilters: () => void;
    /** Page size the grid is currently showing; refetches here must keep it. */
    rowsPerPage: number;
}

// Skylab SDK event types
interface SkylabSelectEventDetail {
    item: {
        value: string;
        label?: string;
    };
    isDeselectAll?: boolean;
}

interface SkylabSelectEvent extends CustomEvent {
    detail: SkylabSelectEventDetail;
}

// State/Region type from commonSlice
interface RegionState {
    id: string;
    name: string;
    initials: string;
    code?: string;
    country?: {
        id: number;
        name: string;
        initials: string;
        abbreviation: string;
    };
}

const ECMSFilter = React.memo(
    ({
        hasFilterChips,
        openFilters,
        onToggleFilters,
        rowsPerPage,
    }: ECMSFilterProps): React.JSX.Element => {
        const dispatch = useDispatch<AppDispatch>();
        const ecmsFiltersState = useSelector(selectEcmsFiltersState);
        const isLoading = useSelector(selectEcmsIsLoading);
        const states = useSelector(selectStates, shallowEqual) as RegionState[];

        const [filtersApplied, setFiltersApplied] = useState(false);
        const [appliedFiltersSnapshot, setAppliedFiltersSnapshot] = useState<EcmsFiltersState>(
            {} as EcmsFiltersState
        );

        const filterClass = classnames({
            "margin-top-xl": !hasFilterChips,
            "margin-right-lg": true,
        });

        // Get list of countries - using US and CA for now
        const countries = useMemo(
            () => [
                {
                    value: "US",
                    label: "United States",
                    selected: ecmsFiltersState.country?.includes("US") || false,
                },
                {
                    value: "CA",
                    label: "Canada",
                    selected: ecmsFiltersState.country?.includes("CA") || false,
                },
            ],
            [ecmsFiltersState.country]
        );

        // Get regions list from states with selected status based on filter state
        const regions = useMemo(() => {
            if (!states || states.length === 0) {
                return [];
            }

            return states.map((state: RegionState) => {
                const stateCode = state.initials || state.code || "";
                return {
                    value: stateCode,
                    label: state.name || "",
                    selected: ecmsFiltersState.region?.includes(stateCode) || false,
                };
            });
        }, [states, ecmsFiltersState.region]);

        const dismissFilters = (e: Event): void => {
            if ((e?.target as HTMLElement)?.tagName?.toLowerCase() === "s-filters-panel") {
                onToggleFilters();
            }
        };

        const applyFilters = useCallback(() => {
            // Check if any filters are active
            const hasActiveFilters =
                ecmsFiltersState.companyId.length > 0 ||
                ecmsFiltersState.customerName.length > 0 ||
                ecmsFiltersState.customerCode.length > 0 ||
                ecmsFiltersState.avaCertId.length > 0 ||
                ecmsFiltersState.exemptCertId.length > 0 ||
                ecmsFiltersState.country.length > 0 ||
                ecmsFiltersState.region.length > 0;

            dispatch(setHasFilterChips(hasActiveFilters));
            dispatch(
                fetchEcms({
                    top: rowsPerPage,
                    skip: 0,
                })
            );
            setFiltersApplied(true);
            setAppliedFiltersSnapshot({ ...ecmsFiltersState });
        }, [dispatch, ecmsFiltersState, rowsPerPage]);

        const dismissChip = useCallback(
            (key: string) => {
                // Reset the filter to empty value
                const updatedState: Partial<EcmsFiltersState> = {
                    [key]: Array.isArray(ecmsFiltersState[key as keyof EcmsFiltersState]) ? [] : "",
                };

                dispatch(setEcmsFiltersState(updatedState));

                // Update snapshot immediately with the new state
                const newSnapshot = {
                    ...appliedFiltersSnapshot,
                    ...updatedState,
                };
                setAppliedFiltersSnapshot(newSnapshot);

                // Check if any filters remain active after this dismissal
                const hasRemainingFilters = Object.entries(newSnapshot).some(([k, v]) => {
                    if (k === key) return false; // Skip the just-dismissed filter
                    return Array.isArray(v) ? v.length > 0 : (v as string).length > 0;
                });

                dispatch(setHasFilterChips(hasRemainingFilters));

                // Re-apply filters after updating state
                setTimeout(() => {
                    dispatch(fetchEcms({ top: rowsPerPage, skip: 0 }));
                }, 100);
            },
            [dispatch, ecmsFiltersState, appliedFiltersSnapshot, rowsPerPage]
        );

        const resetFilters = useCallback(() => {
            preserveScrollPosition(() => {
                dispatch(clearEcmsFiltersState());
                dispatch(setHasFilterChips(false));
                setFiltersApplied(false);
                setAppliedFiltersSnapshot({} as EcmsFiltersState);
                dispatch(fetchEcms({ top: rowsPerPage, skip: 0 }));
            });
        }, [dispatch, rowsPerPage]);

        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
            const { id, value } = e.target;
            dispatch(setEcmsFiltersState({ [id]: value }));
        };

        const handleSelectAdd =
            (fieldName: string) =>
            (e: Event): void => {
                const customEvent = e as SkylabSelectEvent;
                const item = customEvent.detail?.item;

                if (item?.value) {
                    const currentValues = ecmsFiltersState[
                        fieldName as keyof EcmsFiltersState
                    ] as string[];
                    const updatedValues = [...(currentValues || []), item.value];
                    dispatch(setEcmsFiltersState({ [fieldName]: updatedValues }));
                }
            };

        const handleSelectRemove =
            (fieldName: string) =>
            (e: Event): void => {
                const customEvent = e as SkylabSelectEvent;
                const item = customEvent.detail?.item;
                const isDeselectAll = customEvent.detail?.isDeselectAll;

                if (isDeselectAll) {
                    dispatch(setEcmsFiltersState({ [fieldName]: [] }));
                } else if (item?.value) {
                    const currentValues = ecmsFiltersState[
                        fieldName as keyof EcmsFiltersState
                    ] as string[];
                    const updatedValues = (currentValues || []).filter(val => val !== item.value);
                    dispatch(setEcmsFiltersState({ [fieldName]: updatedValues }));
                }
            };

        return (
            <React.Fragment>
                <ECMSFilterChip
                    ecmsFiltersState={appliedFiltersSnapshot}
                    showFilters={filtersApplied}
                    onDismissChip={dismissChip}
                />

                <SFiltersPanel
                    open={openFilters}
                    id="ecms-filters-panel"
                    onS-dismiss={dismissFilters}
                    onS-reset={resetFilters}
                    onS-apply={applyFilters}
                    className={filterClass}
                    data-testid={testIDs.ecms.filtersPanel.container}>
                    <div slot="filters-panel-content">
                        <SRow data-testid={testIDs.ecms.filtersPanel.filters}>
                            <SCol span="12" className="pad-bottom-sm">
                                <label htmlFor="companyId">Avatax Company ID</label>
                                <input
                                    id="companyId"
                                    type="text"
                                    value={ecmsFiltersState.companyId}
                                    onChange={handleInputChange}
                                    disabled={isLoading}
                                    data-testid={testIDs.ecms.filtersPanel.companyId}
                                />
                            </SCol>
                            <SCol span="12" className="pad-bottom-sm">
                                <label htmlFor="customerName">Customer Name</label>
                                <input
                                    id="customerName"
                                    type="text"
                                    value={ecmsFiltersState.customerName}
                                    onChange={handleInputChange}
                                    disabled={isLoading}
                                    data-testid={testIDs.ecms.filtersPanel.customerName}
                                />
                            </SCol>
                            <SCol span="12" className="pad-bottom-sm">
                                <label htmlFor="customerCode">Customer Code</label>
                                <input
                                    id="customerCode"
                                    type="text"
                                    value={ecmsFiltersState.customerCode}
                                    onChange={handleInputChange}
                                    disabled={isLoading}
                                    data-testid={testIDs.ecms.filtersPanel.customerCode}
                                />
                            </SCol>
                            <SCol span="12" className="pad-bottom-sm">
                                <label htmlFor="avaCertId">CC Cert ID</label>
                                <input
                                    id="avaCertId"
                                    type="text"
                                    value={ecmsFiltersState.avaCertId}
                                    onChange={handleInputChange}
                                    disabled={isLoading}
                                    data-testid={testIDs.ecms.filtersPanel.avaCertId}
                                />
                            </SCol>
                            <SCol span="12" className="pad-bottom-sm">
                                <label htmlFor="exemptCertId">ECMS Cert ID</label>
                                <input
                                    id="exemptCertId"
                                    type="text"
                                    value={ecmsFiltersState.exemptCertId}
                                    onChange={handleInputChange}
                                    disabled={isLoading}
                                    data-testid={testIDs.ecms.filtersPanel.exemptCertId}
                                />
                            </SCol>
                            <SCol span="12" className="pad-bottom-sm">
                                <label htmlFor="country-select">Issuing Country</label>
                                <SSelect
                                    inputId="country-select"
                                    multiple
                                    selectionOptional
                                    optionsList={countries}
                                    value={ecmsFiltersState.country || []}
                                    onS-select={handleSelectAdd("country")}
                                    onS-deselect={handleSelectRemove("country")}
                                    disabled={isLoading}
                                    data-testid={testIDs.ecms.filtersPanel.country}
                                />
                            </SCol>
                            <SCol span="12" className="pad-bottom-sm">
                                <label htmlFor="region-select">Regions Applicable</label>
                                <SSelect
                                    inputId="region-select"
                                    multiple
                                    selectionOptional
                                    optionsList={regions}
                                    value={ecmsFiltersState.region || []}
                                    onS-select={handleSelectAdd("region")}
                                    onS-deselect={handleSelectRemove("region")}
                                    predictiveSearch={true}
                                    showSelectionCount={
                                        Array.isArray(ecmsFiltersState.region) &&
                                        ecmsFiltersState.region.length > 4
                                    }
                                    data-testid={testIDs.ecms.filtersPanel.region}
                                />
                            </SCol>
                        </SRow>
                    </div>
                </SFiltersPanel>
            </React.Fragment>
        );
    }
);

export default ECMSFilter;
